import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import {
    apiFactory,
    type ArtifactMetaDataResponse,
    type ArtifactMintSteps,
    Artifacts,
    type ResultFail,
    type ResultSuccess,
} from '@app/client';
import {
    type ArtifactBreakdownResponse,
    type ArtifactMintDisplayItem,
    type ArtifactsResponse,
} from '@app/store/types/artifacts';
import {
    ACTION_ARTIFACT_MINT_PANEL_STEP,
    ACTION_SET_ARTIFACT_MINT_PANEL_OPEN,
    type ArtifactsAction,
    SET_ARTIFACTS,
    SET_ARTIFACTS_BREAKDOWN,
    SET_ARTIFACTS_METADATA,
    SET_SELECTED_ARTIFACT_MINT_DISPLAY,
    SET_USER_ARTIFACTS,
} from '@app/store/types/artifacts-actions';

import { type RootState } from '../reducers';

function setArtifacts(artifacts: ArtifactsResponse): ArtifactsAction {
    return {
        type: SET_ARTIFACTS,
        artifacts,
    };
}

export function setUserArtifacts(userArtifacts: ArtifactBreakdownResponse): ArtifactsAction {
    return {
        type: SET_USER_ARTIFACTS,
        userArtifacts,
    };
}

function setArtifactMetadata(artifactMetadata: ArtifactMetaDataResponse[]): ArtifactsAction {
    return {
        type: SET_ARTIFACTS_METADATA,
        artifactMetadata,
    };
}

function setArtifactsBreakDown(artifactsBreakdown: ArtifactBreakdownResponse): ArtifactsAction {
    return {
        type: SET_ARTIFACTS_BREAKDOWN,
        artifactsBreakdown,
    };
}

export function loadArtifactsData() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch(getArtifactMetaData());
        dispatch(getArtifactsCollection());
        dispatch(getArtifactsBreakdown());
    };
}

function getArtifactsCollection() {
    return function (
        dispatch: ThunkDispatch<RootState, void, Action>,
    ): Promise<ResultSuccess<ArtifactBreakdownResponse> | ResultFail | void> | void {
        return apiFactory()
            .call(Artifacts.getCollection, {})
            .then((r) => {
                if (r.ok) {
                    dispatch(setArtifacts(r.value));
                }
            });
    };
}

function getArtifactsBreakdown() {
    return function (
        dispatch: ThunkDispatch<RootState, void, Action>,
    ): Promise<ResultSuccess<ArtifactBreakdownResponse> | ResultFail | void> | void {
        return apiFactory()
            .call(Artifacts.getBreakdown, {})
            .then((r) => {
                if (r.ok) {
                    dispatch(setArtifactsBreakDown(r.value));
                }
            });
    };
}

export function setArtifactMintPanelOpen(artifactMintPanelOpen: boolean): ArtifactsAction {
    return {
        type: ACTION_SET_ARTIFACT_MINT_PANEL_OPEN,
        artifactMintPanelOpen,
    };
}

export function setArtifactMintPanelStep(artifactMintPanelStep: ArtifactMintSteps | null): ArtifactsAction {
    return {
        type: ACTION_ARTIFACT_MINT_PANEL_STEP,
        artifactMintPanelStep,
    };
}

export function setSelectedArtifactMintDisplay(selectedArtifact: ArtifactMintDisplayItem | null): ArtifactsAction {
    return {
        type: SET_SELECTED_ARTIFACT_MINT_DISPLAY,
        selectedArtifact,
    };
}

function getArtifactMetaData() {
    return function (
        dispatch: ThunkDispatch<RootState, void, Action>,
    ): Promise<ResultSuccess<ArtifactBreakdownResponse> | ResultFail | void> | void {
        apiFactory()
            .call(Artifacts.getCollectionMetadata, {})
            .then((r) => {
                if (r.ok) {
                    const res = r.value;
                    dispatch(setArtifactMetadata(res));
                }
            });
    };
}
